/**
 * PrestigeSystem — New Game+ prestige system for Spellroads.
 * When the party reaches level 20 (or defeats the final boss), they can Prestige:
 * reset the world while keeping a chosen carry-over bonus and incrementing prestige count.
 *
 * @module PrestigeSystem
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';

// ─── Prestige bonus pool ──────────────────────────────────────────────────────

/**
 * Full pool of prestige bonuses. Player picks 1 of 3 offered at prestige time.
 * @type {Array<{ id: string, name: string, description: string }>}
 */
const BONUS_POOL = [
  {
    id: 'veteran_start',
    name: 'Veteran Start',
    description: 'Party starts at level 3 instead of level 1.',
  },
  {
    id: 'gold_reserve',
    name: 'Gold Reserve',
    description: 'Start with 500 gold instead of 200.',
  },
  {
    id: 'heirloom_weapon',
    name: 'Heirloom Weapon',
    description: 'One random rare weapon in your starting inventory.',
  },
  {
    id: 'fame_head_start',
    name: 'Fame Head Start',
    description: 'Start with 100 fame (Renowned) instead of Unknown.',
  },
  {
    id: 'bonus_skill_point',
    name: 'Bonus Skill Point',
    description: 'Each character starts with 1 extra skill point to spend.',
  },
  {
    id: 'swift_travel',
    name: 'Swift Travel',
    description: 'Party moves 25% faster on the world map.',
  },
  {
    id: 'fortified_start',
    name: 'Fortified Start',
    description: 'All characters start with +10% maximum HP.',
  },
  {
    id: 'merchant_friend',
    name: 'Merchant Friend',
    description: 'All shop prices are 15% cheaper.',
  },
  {
    id: 'lucky_drops',
    name: 'Lucky Drops',
    description: 'Enemy loot quality tier is increased by +1.',
  },
  {
    id: 'veteran_companions',
    name: 'Veteran Companions',
    description: 'Hireable companions start at level 2 instead of level 1.',
  },
];

// ─── PrestigeSystem ───────────────────────────────────────────────────────────

const PrestigeSystem = {
  /** @type {number} Current prestige level (0 = no prestige yet) */
  prestige: 0,

  /** @type {Array<{ id: string, name: string, description: string }>} Active prestige bonuses (one per prestige) */
  bonuses: [],

  /** @type {boolean} Whether the prestige-available toast has been shown for this run */
  _toastShown: false,

  // ── Init ──────────────────────────────────────────────────────────────────

  init() {
    this.prestige = 0;
    this.bonuses = [];
    this._toastShown = false;
    DebugLog.info('[prestige] PrestigeSystem initialized');
  },

  // ── Eligibility ───────────────────────────────────────────────────────────

  /**
   * Returns true if the party meets the prestige condition (avg level >= 20).
   * @returns {boolean}
   */
  canPrestige() {
    try {
      const CharSystem = window.__characterSystem;
      if (!CharSystem) return false;

      const heroes = CharSystem.getAll?.().filter(c => c.isHero) ?? [];
      if (heroes.length === 0) return false;

      const avgLevel = heroes.reduce((sum, c) => sum + (c.level ?? 1), 0) / heroes.length;
      return avgLevel >= 20;
    } catch (err) {
      DebugLog.warn(`[prestige] canPrestige check failed: ${err.message}`);
      return false;
    }
  },

  // ── Bonus selection ───────────────────────────────────────────────────────

  /**
   * Returns 3 randomly selected bonus options from the pool.
   * Already-active bonuses are excluded from the offered choices.
   * @returns {Array<{ id: string, name: string, description: string }>}
   */
  getAvailableBonuses() {
    const activeIds = new Set(this.bonuses.map(b => b.id));
    const candidates = BONUS_POOL.filter(b => !activeIds.has(b.id));

    // Fisher-Yates shuffle then take first 3
    const shuffled = [...candidates];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled.slice(0, 3);
  },

  // ── Prestige action ───────────────────────────────────────────────────────

  /**
   * Perform prestige: increment prestige count, record chosen bonus, reset world systems.
   * @param {{ id: string, name: string, description: string }} selectedBonus
   * @returns {{ ok: boolean, message: string }}
   */
  doPrestige(selectedBonus) {
    if (!selectedBonus || !selectedBonus.id) {
      return { ok: false, message: 'Invalid bonus selection' };
    }

    this.prestige += 1;
    this.bonuses.push(selectedBonus);
    this._toastShown = false;

    DebugLog.info(`[prestige] Prestige ${this.prestige} activated; bonus=${selectedBonus.id}`);

    // Reset world-level systems
    try {
      const WorldState = window.__worldState;
      if (WorldState) {
        WorldState.activeQuests     = [];
        WorldState.completedQuests  = [];
        WorldState.partyFame        = this._prestigeStartFame();
        WorldState.partyGold        = this._prestigeStartGold();
        WorldState.rationPoints     = 20;
        WorldState.currentTown      = null;
        WorldState.dungeonState     = null;
        DebugLog.info('[prestige] WorldState soft-reset');
      }
    } catch (err) {
      DebugLog.warn(`[prestige] WorldState reset error: ${err.message}`);
    }

    try {
      const QuestSystem = window.__questSystem;
      if (QuestSystem?.reset) QuestSystem.reset();
    } catch { /* optional */ }

    try {
      const FameSystem = window.__fameSystem;
      if (FameSystem?.reset) FameSystem.reset();
    } catch { /* optional */ }

    EventBus.emit('prestige:complete', { prestige: this.prestige, bonus: selectedBonus });

    return {
      ok: true,
      message: `Prestige ${this.prestige} achieved! Bonus: "${selectedBonus.name}" — ${selectedBonus.description}`,
    };
  },

  // ── Bonus application helpers ─────────────────────────────────────────────

  /**
   * Starting gold amount after applying prestige bonuses.
   * @returns {number}
   */
  _prestigeStartGold() {
    return this.hasBonusActive('gold_reserve') ? 500 : 200;
  },

  /**
   * Starting fame after applying prestige bonuses.
   * @returns {number}
   */
  _prestigeStartFame() {
    return this.hasBonusActive('fame_head_start') ? 100 : 0;
  },

  // ── State queries ─────────────────────────────────────────────────────────

  /**
   * Returns the most recently selected active bonus, or null.
   * @returns {{ id: string, name: string, description: string }|null}
   */
  getActiveBonus() {
    return this.bonuses.length > 0 ? this.bonuses[this.bonuses.length - 1] : null;
  },

  /**
   * Returns the current prestige level (0 = not yet prestiged).
   * @returns {number}
   */
  getPrestigeLevel() {
    return this.prestige;
  },

  /**
   * Returns true if a bonus with the given id is active.
   * @param {string} bonusId
   * @returns {boolean}
   */
  hasBonusActive(bonusId) {
    return this.bonuses.some(b => b.id === bonusId);
  },

  /**
   * Returns a human-readable summary of prestige state.
   * @returns {{ prestige: number, bonuses: string[], latestBonus: string|null }}
   */
  getSummary() {
    return {
      prestige:    this.prestige,
      bonuses:     this.bonuses.map(b => b.name),
      latestBonus: this.getActiveBonus()?.name ?? null,
    };
  },

  // ── Serialisation (for SaveSystem) ────────────────────────────────────────

  /**
   * Returns a plain object snapshot suitable for JSON serialisation.
   * @returns {{ prestige: number, bonuses: Array }}
   */
  serialize() {
    return {
      prestige: this.prestige,
      bonuses:  [...this.bonuses],
    };
  },

  /**
   * Restore prestige state from a plain object snapshot.
   * @param {{ prestige: number, bonuses: Array }} data
   */
  restore(data) {
    if (!data) return;
    this.prestige = data.prestige ?? 0;
    this.bonuses  = Array.isArray(data.bonuses) ? [...data.bonuses] : [];
    this._toastShown = false;
    DebugLog.info(`[prestige] Prestige state restored; level=${this.prestige} bonuses=${this.bonuses.length}`);
  },

  // ── Periodic check (called from game loop, not every frame) ──────────────

  /**
   * Check prestige eligibility and emit a toast once if condition is met.
   * Intended to be called on a slow interval (e.g. once per minute).
   */
  tick() {
    if (this._toastShown) return;
    if (this.canPrestige()) {
      this._toastShown = true;
      EventBus.emit('prestige:available', {});
      DebugLog.info('[prestige] Prestige available — party avg level >= 20');
    }
  },
};

export default PrestigeSystem;
